export class StorageService {
	/**
	 * Reads a JSON value from localStorage.
	 * @param key - storage key
	 * @param defaultValue - returned when the key is absent or holds invalid JSON
	 * @returns the parsed value; the literal string `undefined` yields `undefined`
	 */
	static getJson<T>(key: string, defaultValue: T): T {
		const item = localStorage.getItem(key);
		if (item === null) return defaultValue;
		if (item === 'undefined') return undefined as T;
		try {
			return JSON.parse(item) as T;
		} catch {
			return defaultValue;
		}
	}

	/**
	 * Serialises a value as JSON into localStorage.
	 * @param key - storage key
	 * @param value - value to store
	 */
	static setJson<T>(key: string, value: T): void {
		localStorage.setItem(key, JSON.stringify(value));
	}

	/**
	 * @param key - storage key
	 * @returns the raw string value or `null`
	 */
	static getItem(key: string): string | null {
		return localStorage.getItem(key);
	}

	/**
	 * @param key - storage key
	 * @param value - raw string value
	 */
	static setItem(key: string, value: string): void {
		localStorage.setItem(key, value);
	}

	/** @param key - storage key to delete */
	static remove(key: string): void {
		localStorage.removeItem(key);
	}

	/** Removes every localStorage entry. */
	static clear(): void {
		localStorage.clear();
	}

	/** @returns whether a scratch IndexedDB database can be opened */
	static checkIndexedDbSupport(): Promise<boolean> {
		const dbName = `checkIndexedDBSupport-${Date.now()}`;
		return new Promise((resolve) => {
			let request: IDBOpenDBRequest;
			try {
				request = indexedDB.open(dbName);
			} catch {
				resolve(false);
				return;
			}
			request.onsuccess = () => {
				request.result.close();
				resolve(true);
				indexedDB.deleteDatabase(dbName);
			};
			request.onerror = () => {
				resolve(false);
				indexedDB.deleteDatabase(dbName);
			};
		});
	}

	/** Deletes every IndexedDB database of this origin. */
	static async deleteAllIndexedDb(): Promise<void> {
		const dbs = await window.indexedDB.databases();
		dbs.forEach(({ name }) => {
			if (name) window.indexedDB.deleteDatabase(name);
		});
	}
}
